import { AppSettings } from "../Constants/AppSettings"
import {baseResponse} from "../DataTypes/baseResponse"
import { fetchData } from "../Helpers/FetchData"

export class UserDataProvider {

    // HERO METHODS
    static hero(body: any){
            return fetchData(
                'hero',
                'POST',
                body
            )
        }

    static lookupRequest(body: any){
            return fetchData(
                'herolookup',
                'POST',
                body
            )
        }
    static getOffers(body: any){
            return fetchData(
                'hero_offers',
                'POST',
                body
            )
        }
    static heroUpdateInfo(body: any){
        return fetchData(
            'heroupdateinfo',
            'POST',
            body
        )
    }

    static googleAuthorization(body: any){
        return fetchData(
            'auth/google',
            'POST',
            body
        )
    }

    static authorization(body: any){
        return fetchData(
            'auth/login',
            'POST',
            body
        )
    }

    static register(body: any){
        return fetchData(
            'auth/register',
            'POST',
            body
        )
    }

    static logout(body: any){
        return fetchData(
            'auth/logout',
            'POST',
            body
        )
    }

    static userInfo(body: any){
        return fetchData(
            'auth/profile',
            'POST',
            body
        )
    }

    // Tells the frontend whether to show lead-management UI at all, and
    // whether to show admin-only controls (editing the shared signature
    // template, publishing email templates, etc).
    static checkLeadAccess = (body:any) => {
        return fetchData(
            'outreach/access/check',
            'POST',
            body
        )
    }
    
    static listLeadManagers = (body:any) => {
        return fetchData(
            'outreach/access/managers',
            'POST',
            body
        )
    }

    // RESEARCH — Google Places search, review, and add-as-leads.
    static searchResearchPlaces = (body:any) => {
        return fetchData(
            'outreach/research/search',
            'POST',
            body
        )
    }

    static addLeadsFromResearch = (body:any) => {
        return fetchData(
            'outreach/research/add',
            'POST',
            body
        )
    }

    static runResearchBotNow = (body:any) => {
        return fetchData(
            'outreach/research/prefetch/run',
            'POST',
            body
        )
    }

    static loadLeads = (body:any) => {
        return fetchData(
            'outreach/mine',
            'POST',
            body
        )
    }

    static addLead = (body:any) => {
        return fetchData(
            'outreach/add',
            'POST',
            body
        )
    }

    static getLeadDetail = (leadId: number, body:any) => {
        return fetchData(
            `outreach/${leadId}`,
            'POST',
            body
        )
    }

    static editLead = (leadId: number, body:any) => {
        return fetchData(
            `outreach/${leadId}/edit`,
            'POST',
            body
        )
    }

    static updateLeadStatus = (leadId: number, body:any) => {
        return fetchData(
            `outreach/${leadId}/status`,
            'POST',
            body
        )
    }

    static getLeadCategories = (body:any, includeInactive: boolean = false) => {
        // get_lead_categories reads include_inactive off the query string
        // regardless of HTTP method, so it has to go in the URL, not the body.
        return fetchData(
            `outreach/categories${includeInactive ? '?include_inactive=true' : ''}`,
            'POST',
            body
        )
    }

    static addLeadCategory = (body:any) => {
        return fetchData(
            'outreach/categories/add',
            'POST',
            body
        )
    }

    static editLeadCategory = (categoryId: number, body:any) => {
        return fetchData(
            `outreach/categories/${categoryId}/edit`,
            'POST',
            body
        )
    }

    static deleteLeadCategory = (categoryId: number, body:any) => {
        return fetchData(
            `outreach/categories/${categoryId}/delete`,
            'POST',
            body
        )
    }

    // MAIL — settings
    static getEmailSettings = (body:any) => {
        return fetchData(
            'outreach/mail/settings',
            'POST',
            body
        )
    }

    static updateEmailSettings = (body:any) => {
        return fetchData(
            'outreach/mail/settings/update',
            'POST',
            body
        )
    }

    static testEmailConnection = (body:any) => {
        return fetchData(
            'outreach/mail/settings/test',
            'POST',
            body
        )
    }

    // MAIL — reusable send templates
    static getEmailTemplates = (body:any) => {
        return fetchData(
            'outreach/mail/templates',
            'POST',
            body
        )
    }

    static addEmailTemplate = (body:any) => {
        return fetchData(
            'outreach/mail/templates/add',
            'POST',
            body
        )
    }

    static editEmailTemplate = (templateId: number, body:any) => {
        return fetchData(
            `outreach/mail/templates/${templateId}/edit`,
            'POST',
            body
        )
    }

    static deleteEmailTemplate = (templateId: number, body:any) => {
        return fetchData(
            `outreach/mail/templates/${templateId}/delete`,
            'POST',
            body
        )
    }

    static previewEmailTemplate = (templateId: number, body:any) => {
        return fetchData(
            `outreach/mail/templates/${templateId}/preview`,
            'POST',
            body
        )
    }

    // MAIL — shared signature template (GET-only endpoint on the backend, so
    // the auth token has to travel as a query param — fetchData never
    // attaches a body or an Authorization header to GET requests).
    static getSharedSignature = (userToken: string) => {
        return fetchData(
            `outreach/mail/signature/${encodeURIComponent(userToken)}`,
            'GET'
        )
    }

    static updateSharedSignature = (body:any) => {
        return fetchData(
            'outreach/mail/signature/update',
            'POST',
            body
        )
    }

    // MAIL — inbox
    static syncInbox = (body:any) => {
        return fetchData(
            'outreach/mail/sync',
            'POST',
            body
        )
    }

    static sendEmail = (body:any) => {
        return fetchData(
            'outreach/mail/send',
            'POST',
            body
        )
    }

    static triggerCampaignSend = (body:any) => {
        return fetchData(
            'outreach/mail/campaigns/send',
            'POST',
            body
        )
    }

    static listEmails = (body:any) => {
        return fetchData(
            'outreach/mail',
            'POST',
            body
        )
    }

    static getEmailDetail = (emailId: number, body:any) => {
        return fetchData(
            `outreach/mail/${emailId}`,
            'POST',
            body
        )
    }

    // Entire history of the conversation this email belongs to, resolved
    // server-side — replaces the old client-side lead_id grouping.
    static getEmailThread = (emailId: number, body:any) => {
        return fetchData(
            `outreach/mail/${emailId}/all`,
            'POST',
            body
        )
    }

    // Every email ever linked to one lead, across every mailbox — not just
    // the current user's own EmailAccount.
    static getLeadEmailTimeline = (leadId: number, body:any) => {
        return fetchData(
            `outreach/${leadId}/emails`,
            'POST',
            body
        )
    }

    static markEmailRead = (emailId: number, body:any) => {
        return fetchData(
            `outreach/mail/${emailId}/read`,
            'POST',
            body
        )
    }

    static markEmailUnread = (emailId: number, body:any) => {
        return fetchData(
            `outreach/mail/${emailId}/unread`,
            'POST',
            body
        )
    }

    static markAllReplyNotificationsRead = (body: any) => {
        return fetchData('outreach/mail/notifications/replies/mark-all-read', 'POST', body)
    }

    static deleteEmail = (emailId: number, body:any) => {
        return fetchData(
            `outreach/mail/${emailId}/delete`,
            'POST',
            body
        )
    }

    // STATUS LOGS — the backend accepts GET or POST on these two, but every
    // other read in this app sends the auth token in a POST body, so these
    // follow the same convention rather than special-casing GET.
    static getReplyNotifications = (body: any) => {
        const { limit } = body || {}
        const qs = limit ? `?limit=${limit}` : ''
        return fetchData(`outreach/notifications/replies/${body.userToken}${qs}`, 'GET')
    }
    
    static listStatusLogs = (body:any) => {
        return fetchData(
            'outreach/status-logs',
            'POST',
            body
        )
    }

    static markStatusLogRead = (logId: number, body:any) => {
        return fetchData(
            `outreach/status-logs/${logId}/read`,
            'POST',
            body
        )
    }

    static markAllStatusLogsRead = (body:any) => {
        return fetchData(
            'outreach/status-logs/mark-all-read',
            'POST',
            body
        )
    }

    static getUnreportedStatusLogs = (body:any) => {
        return fetchData(
            'outreach/status-logs/unreported',
            'POST',
            body
        )
    }

    // Soft-delete — same URL convention as .../read (<id>/<action>).
    static trashStatusLog = (logId: number, body:any) => {
        return fetchData(
            `outreach/status-logs/${logId}/trash`,
            'POST',
            body
        )
    }

    static restoreStatusLog = (logId: number, body:any) => {
        return fetchData(
            `outreach/status-logs/${logId}/restore`,
            'POST',
            body
        )
    }

    // TELEGRAM
    static saveTelegramChatId = (body:any) => {
        return fetchData(
            'outreach/telegram/chat-id',
            'POST',
            body
        )
    }

    static sendTelegramReport = (body:any) => {
        return fetchData(
            'outreach/telegram/report/send',
            'POST',
            body
        )
    }

    static listTelegramReports = (body:any) => {
        return fetchData(
            'outreach/telegram/reports',
            'POST',
            body
        )
    }

    static getTelegramReportDetail = (reportId: number, body:any) => {
        return fetchData(
            `outreach/telegram/reports/${reportId}`,
            'POST',
            body
        )
    }
    
    
    
    static getWorkers = (body:any) => {
        return fetchData(
            `outreach/leads/admin/workers/`,
            'POST',
            body
        )
    }
    
    static getActivityDashboard = (body:any) => {
        return fetchData(
            `outreach/leads/admin/activity-dashboard/`,
            'POST',
            body
        )
    }
    
    
    static getMyEmailStats = (body:any) => {
        return fetchData(
            `outreach/leads/my-email-stats/`,
            'POST',
            body
        )
    }
    
    
    static getMyDashboard = (body:any) => {
        return fetchData(
            `outreach/leads/my-dashboard/`,
            'POST',
            body
        )
    }
    
    // ── Salary configs (admin only) ──
 
    static getSalaryConfigs = (body: any) => {
        return fetchData(
            `outreach/salary/configs`,
            'POST',
            body
        )
    }
 
    static setSalaryConfig = (body: any) => {
        return fetchData(
            `outreach/salary/configs/set`,
            'POST',
            body
        )
    }
 
    // ── Issuing (admin only) ──
 
    static getCompensationPreview = (body: any) => {
        return fetchData(
            `outreach/compensation/preview`,
            'POST',
            body
        )
    }
 
    static issueCompensation = (body: any) => {
        return fetchData(
            `outreach/compensation/issue`,
            'POST',
            body
        )
    }
 
    // ── Viewing (admin: all; worker: own) ──
 
    static listCompensations = (body: any) => {
        return fetchData(
            `outreach/compensation/list`,
            'POST',
            body
        )
    }
 
    // compensationId is pulled out of body and baked into the URL — the
    // rest of body (just userToken here) still goes through as the
    // POST payload, same as get_lead_detail / get_email_detail do on
    // the Django side.
    static getCompensationDetail = (body: any) => {
        const { compensationId, ...rest } = body
        return fetchData(
            `outreach/compensation/${compensationId}`,
            'POST',
            rest
        )
    }
 
    // ── Invoice templates (admin only) ──
 
    static getInvoiceTemplates = (body: any) => {
        return fetchData(
            `outreach/invoice-templates`,
            'POST',
            body
        )
    }
 
    static saveInvoiceTemplate = (body: any) => {
        return fetchData(
            `outreach/invoice-templates/save`,
            'POST',
            body
        )
    }

    // ── Contact requests (admin only) ──
    static getContactRequests = (body: any) => {
        return fetchData(
            'outreach/requests/list/',
            'POST',
            body
        )
    }

    // Opening a request's detail marks it as read on the backend.
    static getContactRequestDetail = (body: any) => {
        return fetchData(
            'outreach/requests/detail/',
            'POST',
            body
        )
    }

    static updateContactRequest = (body: any) => {
        return fetchData(
            'outreach/requests/update/',
            'POST',
            body
        )
    }

    static getNewRequestLogs = (body: any) => {
        return fetchData(
            'outreach/requests/new-logs/',
            'POST',
            body
        )
    }

}

export async function loadData(dataProvider: (body:any)=>any, body: {}) : Promise<baseResponse> {
    try { 
        const response:baseResponse = await dataProvider(body)
        if(response.statusCode<200){ // system error exception
            // console.error("STATUS CODE", response.statusMessage)
        }
        return response
    } catch(e){
        console.log('error', e)
        throw e
    }
}